import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import ApiService from '../services/ApiService';
import './AdminUpload.css';

/**
 * AdminUpload Component
 * Allows segment admins to upload Excel files with session data
 * Requires event selection before upload (per spec T074)
 */
const AdminUpload = () => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Auth guard — super_admin only
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/login', { state: { from: '/admin/legacy' } });
      } else if (user && user.role !== 'super_admin') {
        // Segment admins go back to their own dashboard, not the end-user page
        navigate(user.role === 'admin' ? '/admin' : '/');
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const segments = [
    { code: 'OPS-SW', name: 'OPS-SW' },
    { code: 'GROW_PLM', name: 'Grow@PLM' },
    { code: 'LCS', name: 'LCS' },
    { code: 'ME', name: 'ME' },
    { code: 'PES', name: 'PES' },
    { code: 'SIM', name: 'SIM' },
  ];

  // Fetch active events on mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoadingEvents(true);
        const data = await ApiService.get('/sessions/events');
        // API returns { success, count, data: [...] }
        const allEvents = Array.isArray(data) ? data : (data.data || data.events || []);
        // Only show active events
        const activeEvents = allEvents.filter(evt => (evt.status || evt.eventStatus) !== 'archived');
        setEvents(activeEvents);
      } catch (err) {
        console.error('Failed to load events:', err);
        setError('Failed to load events. Please ensure you are logged in.');
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        setError('Please select an Excel file (.xlsx or .xls)');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedSegment) {
      setError('Please select a business segment');
      return;
    }

    if (!selectedEventId) {
      setError('Please select a quarterly event');
      return;
    }

    if (!file) {
      setError('Please select an Excel file');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const endpoint = `/admin/upload?eventId=${selectedEventId}`;
      const uploadResult = await ApiService.uploadExcel(file, selectedSegment, endpoint);
      setResult(uploadResult);
      setFile(null);
      // Reset file input
      document.getElementById('file-input').value = '';
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px', color: '#999' }}>
        Checking authentication...
      </div>
    );
  }

  // Don't render if not authenticated (redirect is in-progress)
  if (!isAuthenticated) return null;

  return (
    <div className="admin-upload">
      <header className="admin-header">
        <h1>📊 Admin - Session Upload</h1>
        <p>Upload Excel files to import or update learning day sessions</p>
      </header>

      <div className="upload-container">
        <form onSubmit={handleUpload} className="upload-form">
          <div className="form-group">
            <label htmlFor="event-select">
              Quarterly Event <span className="required">*</span>
            </label>
            {loadingEvents ? (
              <p>Loading events...</p>
            ) : events.length === 0 ? (
              <p style={{ color: '#888' }}>No active events found. Ask a Super Admin to create one.</p>
            ) : (
              <select
                id="event-select"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                required
              >
                <option value="">-- Select Event --</option>
                {events.map(evt => (
                  <option key={evt.id} value={evt.id}>
                    {evt.eventName || evt.event_name} ({new Date(evt.eventDate || evt.event_date).toLocaleDateString()})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="segment-select">
              Business Segment <span className="required">*</span>
            </label>
            <select
              id="segment-select"
              value={selectedSegment}
              onChange={(e) => setSelectedSegment(e.target.value)}
              required
            >
              <option value="">-- Select Segment --</option>
              {segments.map(seg => (
                <option key={seg.code} value={seg.code}>
                  {seg.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="file-input">
              Excel File <span className="required">*</span>
            </label>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              required
            />
            {file && <p className="file-name">Selected: {file.name}</p>}
          </div>

          <button
            type="submit"
            className="upload-button"
            disabled={uploading || !selectedEventId || !selectedSegment || !file}
          >
            {uploading ? '⏳ Uploading...' : '📤 Upload Sessions'}
          </button>
        </form>

        {error && (
          <div className="message error-message">
            <h3>❌ Upload Failed</h3>
            <p>{error}</p>
          </div>
        )}

        {result && (
          <div className="message success-message">
            <h3>✅ Upload Complete</h3>
            <div className="result-stats">
              <p><strong>Total Rows:</strong> {result.total}</p>
              <p><strong>Successful:</strong> {result.successful}</p>
              <p><strong>Failed:</strong> {result.failed}</p>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="error-details">
                <h4>Validation Errors:</h4>
                <ul>
                  {result.errors.slice(0, 10).map((err, idx) => {
                    const errorMessages = Array.isArray(err.errors) 
                      ? err.errors.map(e => typeof e === 'string' ? e : e.message || JSON.stringify(e))
                      : [err.errors?.message || err.errors || 'Unknown error'];
                    return (
                      <li key={idx}>
                        <strong>Row {err.row}:</strong> {errorMessages.join(', ')}
                      </li>
                    );
                  })}
                </ul>
                {result.errors.length > 10 && (
                  <p className="more-errors">
                    ... and {result.errors.length - 10} more errors
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="instructions">
          <h3>📋 Excel File Format</h3>
          <p>Your Excel file must contain the following columns:</p>
          <ul>
            <li><strong>Name</strong> - Session name (required)</li>
            <li><strong>Presenter</strong> - Presenter name (required)</li>
            <li><strong>Category</strong> - Session category (required)</li>
            <li><strong>Time CET</strong> - Time in HH:MM-HH:MM format (required)</li>
            <li><strong>Date</strong> - Date in DD-MM-YYYY format (required)</li>
            <li><strong>Description</strong> - Session description (optional)</li>
            <li><strong>Guest Speaker</strong> - Yes/No (optional)</li>
            <li><strong>Recommended Session</strong> - Business segment code (optional)</li>
            <li><strong>MLW Link</strong> or <strong>Saba Link</strong> - External platform URL (optional)</li>
          </ul>
          <p className="note">
            <strong>Note:</strong> Re-uploading sessions with the same Name, Date, Time, and Segment will update existing records.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminUpload;
